import { MAX_FILE_BYTES, MAX_ROWS, MAX_COLUMNS, validateTable } from './bulkEmailModel.js';

/** Parse UTF-8 CSV including quoted commas, escaped quotes and multiline values. */
function parseCsv(text) {
    const rows = []; let row = []; let cell = ''; let quoted = false; let closed = false;
    const pushCell = () => { row.push(cell); cell = ''; closed = false; if (row.length > MAX_COLUMNS) throw new Error('The file contains more than 30 columns.'); };
    const pushRow = () => { pushCell(); rows.push(row); row = []; if (rows.length > MAX_ROWS + 100) throw new Error('The file contains too many rows.'); };
    for (let index = 0; index < text.length; index += 1) {
        const char = text[index];
        if (quoted) {
            if (char === '"' && text[index + 1] === '"') { cell += '"'; index += 1; }
            else if (char === '"') { quoted = false; closed = true; }
            else cell += char;
        } else if (char === ',') pushCell();
        else if (char === '\r' || char === '\n') { if (char === '\r' && text[index + 1] === '\n') index += 1; pushRow(); }
        else if (char === '"' && !cell && !closed) quoted = true;
        else if (closed || char === '"') throw new Error('CSV quoting is invalid. Export the file again as CSV UTF-8.');
        else cell += char;
        if (cell.length > 2000) throw new Error('A spreadsheet cell exceeds 2,000 characters.');
    }
    if (quoted) throw new Error('CSV contains an unclosed quoted value.');
    if (cell || row.length || closed) pushRow();
    return rows;
}

/** ZIP checksums detect corrupt workbook content before it can become recipient data. */
const CRC_TABLE = Array.from({ length: 256 }, (_, index) => {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    return value >>> 0;
});

/** Read the ZIP directory before inflating only necessary XLSX parts, with bounded expansion. */
function xlsxArchive(buffer) {
    const view = new DataView(buffer); const bytes = new Uint8Array(buffer);
    let end = buffer.byteLength - 22;
    while (end >= Math.max(0, buffer.byteLength - 65557) && view.getUint32(end, true) !== 0x06054b50) end -= 1;
    if (end < 0 || view.getUint32(end, true) !== 0x06054b50) throw new Error('This is not a valid XLSX file.');
    const count = view.getUint16(end + 10, true);
    let position = view.getUint32(end + 16, true); let total = 0;
    if (count > 500 || view.getUint16(end + 4, true) !== 0) throw new Error('This workbook is too complex. Export the recipient sheet as CSV.');
    const entries = new Map();
    for (let index = 0; index < count; index += 1) {
        if (view.getUint32(position, true) !== 0x02014b50) throw new Error('Invalid workbook archive.');
        const flags = view.getUint16(position + 8, true); const method = view.getUint16(position + 10, true);
        const crc = view.getUint32(position + 16, true); const compressed = view.getUint32(position + 20, true); const size = view.getUint32(position + 24, true);
        const nameSize = view.getUint16(position + 28, true); const extraSize = view.getUint16(position + 30, true); const commentSize = view.getUint16(position + 32, true);
        const offset = view.getUint32(position + 42, true);
        const name = new TextDecoder().decode(bytes.subarray(position + 46, position + 46 + nameSize));
        if ((flags & 1) || ![0, 8].includes(method) || size > 8 * 1024 * 1024 || entries.has(name)) throw new Error('Encrypted, oversized or unsupported workbook. Save a plain XLSX or CSV file.');
        total += size;
        if (total > 20 * 1024 * 1024) throw new Error('Workbook expands beyond the supported size. Export the recipient sheet as CSV.');
        entries.set(name, { compressed, size, offset, method, crc });
        position += 46 + nameSize + extraSize + commentSize;
    }
    return async name => {
        const entry = entries.get(name);
        if (!entry) return null;
        const { offset, compressed, size, method, crc } = entry;
        if (view.getUint32(offset, true) !== 0x04034b50) throw new Error('Invalid workbook entry.');
        const start = offset + 30 + view.getUint16(offset + 26, true) + view.getUint16(offset + 28, true);
        if (start + compressed > buffer.byteLength) throw new Error('Truncated workbook entry.');
        let stream = new Blob([bytes.subarray(start, start + compressed)]).stream();
        if (method === 8) {
            try { stream = stream.pipeThrough(new window.DecompressionStream('deflate-raw')); }
            catch (error) { throw new Error('This browser cannot read XLSX files. Use a current browser or upload CSV UTF-8.'); }
        }
        const reader = stream.getReader(); const chunks = []; let actual = 0; let checksum = 0xffffffff;
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            actual += value.length;
            if (actual > size || actual > 8 * 1024 * 1024) { await reader.cancel(); throw new Error('Workbook entry exceeds its declared size.'); }
            for (const byte of value) checksum = CRC_TABLE[(checksum ^ byte) & 255] ^ (checksum >>> 8);
            chunks.push(value);
        }
        if (actual !== size || ((checksum ^ 0xffffffff) >>> 0) !== crc) throw new Error('Workbook data is incomplete or corrupt. Export the file again.');
        return new Blob(chunks).text();
    };
}

/** Reject malformed XML and document types; no workbook links or macros are executed. */
function xml(text) {
    if (!text || /<!DOCTYPE|<!ENTITY/i.test(text)) throw new Error('Missing or unsupported workbook XML.');
    const document = new DOMParser().parseFromString(text, 'application/xml');
    if (document.getElementsByTagName('parsererror').length) throw new Error('Workbook XML is invalid.');
    return document;
}

/** Read the first worksheet as values, refusing formulas, hidden data and ambiguous date formats. */
async function parseXlsx(buffer) {
    const read = xlsxArchive(buffer);
    const workbook = xml(await read('xl/workbook.xml'));
    const sheet = workbook.getElementsByTagName('sheet')[0];
    if (!sheet || (sheet.getAttribute('state') && sheet.getAttribute('state') !== 'visible')) throw new Error('The first worksheet must be visible.');
    const relationships = xml(await read('xl/_rels/workbook.xml.rels'));
    const relation = [...relationships.getElementsByTagName('Relationship')].find(item => item.getAttribute('Id') === sheet.getAttribute('r:id'));
    if (!relation || relation.getAttribute('TargetMode') === 'External') throw new Error('Unsupported worksheet reference.');
    const target = relation.getAttribute('Target') || '';
    if (target.includes('..') || target.includes('\\')) throw new Error('Unsupported worksheet path.');
    const path = target.startsWith('/') ? target.slice(1) : `xl/${target}`;
    const worksheet = xml(await read(path));
    if (worksheet.getElementsByTagName('mergeCell').length) throw new Error('Remove merged cells from the recipient worksheet.');
    const stringsText = await read('xl/sharedStrings.xml');
    const strings = stringsText ? [...xml(stringsText).getElementsByTagName('si')].map(item => [...item.getElementsByTagName('t')].map(node => node.textContent).join('')) : [];
    const stylesText = await read('xl/styles.xml');
    const formats = stylesText ? [...(xml(stylesText).getElementsByTagName('cellXfs')[0]?.children || [])].map(item => Number(item.getAttribute('numFmtId'))) : [];
    const rows = [];
    for (const row of worksheet.getElementsByTagName('row')) {
        if (row.getAttribute('hidden') === '1') throw new Error('Unhide or remove hidden rows before uploading.');
        const values = [];
        for (const cell of row.getElementsByTagName('c')) {
            if (cell.getElementsByTagName('f').length) throw new Error('Formulas are not supported. Paste values into a new worksheet first.');
            const letters = /^(\p{Lu}+)\d+$/u.exec(cell.getAttribute('r') || '')?.[1];
            if (!letters) throw new Error('Invalid worksheet cell reference.');
            const column = [...letters].reduce((number, letter) => number * 26 + letter.charCodeAt(0) - 64, 0) - 1;
            const type = cell.getAttribute('t'); const raw = cell.getElementsByTagName('v')[0]?.textContent ?? '';
            let value = raw;
            if (type === 's') {
                if (!/^\d+$/.test(raw) || Number(raw) >= strings.length) throw new Error('Invalid shared text reference.');
                value = strings[Number(raw)];
            } else if (type === 'inlineStr') value = [...cell.getElementsByTagName('t')].map(node => node.textContent).join('');
            else if (type === 'e') throw new Error('The worksheet contains an error cell. Correct it before uploading.');
            else if (raw && (!type || type === 'n') && ![0, 49].includes(formats[Number(cell.getAttribute('s') || 0)] || 0)) throw new Error('Formatted numbers or dates must be converted to text first, or export CSV to preserve their displayed values.');
            if (column >= MAX_COLUMNS) { if (value.trim()) throw new Error('Use no more than 30 columns.'); continue; }
            while (values.length <= column) values.push('');
            values[column] = value;
        }
        if (values.some(value => value.trim())) rows.push(values);
        if (rows.length > MAX_ROWS + 1) throw new Error(`Use no more than ${MAX_ROWS} recipient rows.`);
    }
    // Excel may include styled empty cells after the last header; remove only trailing empties.
    while (rows[0]?.length && !rows[0][rows[0].length - 1].trim()) rows[0].pop();
    rows.slice(1).forEach(row => { while (row.length > (rows[0]?.length || 0) && !row[row.length - 1].trim()) row.pop(); });
    return rows;
}

/** Files remain in browser memory; the API receives only validated rows when Send is confirmed. */
export async function readSpreadsheet(file) {
    if (!file || file.size > MAX_FILE_BYTES || file.size === 0) throw new Error('Choose a non-empty CSV or XLSX file up to 2 MB.');
    let matrix;
    if (/\.csv$/i.test(file.name)) {
        const text = new TextDecoder('utf-8', { fatal: true }).decode(await file.arrayBuffer()).replace(/^\uFEFF/, '');
        matrix = parseCsv(text);
    } else if (/\.xlsx$/i.test(file.name)) matrix = await parseXlsx(await file.arrayBuffer());
    else throw new Error('Choose a .csv or .xlsx file.');
    return validateTable(matrix);
}
